import { DestroyRef, Injectable } from '@angular/core';
import { InputAppMetadataDBFacade, TConnectionStatus } from '@monorepo/core-data';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, timer } from 'rxjs';
import { WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import { share, switchMap, filter, take, timeout, catchError } from 'rxjs/operators';
import {
  TTvDashboardWebsocketRecieveMessage,
  TTvDashboardWebsocketComponentSubscribeMessage,
  TWebSocketConnectReason,
} from '@monorepo/types';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CustomLoggerService } from '../../custom-logging/custom-logger.service';

const HEARTBEAT_INTERVAL_MS = 60_000;
const HEARTBEAT_TIMEOUT_MS = 5_000;
const WIDGET_SILENCE_MS = 5 * 60_000;
const SILENCE_CHECK_INTERVAL_MS = 60_000;

function isHeartbeatAck(msg: TTvDashboardWebsocketRecieveMessage | THearBeatMessage): msg is THeartBeatReceiveMessage {
  return (
    (msg as THeartBeatReceiveMessage).access_type === 'heart_beat' &&
    (msg as THeartBeatReceiveMessage).topic === 'heart_beat'
  );
}

type THeartBeatSendMessage = {
  access_type: 'heart_beat';
  channel_id: 'heart_beat';
  subject_key: string;
  topic: 'heart_beat';
};

type THeartBeatReceiveMessage = {
  access_type: 'heart_beat';
  channel_id: 'heart_beat';
  topic: 'heart_beat';
};

type THearBeatMessage = THeartBeatSendMessage | THeartBeatReceiveMessage;

/**
 * Service responsible for managing WebSocket connections for the TV Dashboard.
 * Handles connection establishment, message sending/receiving, and connection status management.
 */
@Injectable({
  providedIn: 'root',
})
export class TvDashboardWebSocketService {
  private socket$: WebSocketSubject<
    TTvDashboardWebsocketRecieveMessage | TTvDashboardWebsocketComponentSubscribeMessage['message'] | THearBeatMessage
  > | null = null;
  private messagesSubject$ = new Subject<TTvDashboardWebsocketRecieveMessage>();
  public messages$ = this.messagesSubject$.asObservable().pipe(share());

  connectionStatus = new BehaviorSubject<TConnectionStatus>('DISCONNECTED');

  #connectionInfo: [url: string, params: Record<string, string>, subjectKey: string] | null = null;
  #heartBeatTimer$: Subscription | null = null;
  #silenceCheckTimer$: Subscription | null = null;
  #watchedChannels = new Map<string, number>();
  #isServerHealthy = toSignal(this.inputAppMetadataDBFacade.getOnlineStatus(), { initialValue: false });

  constructor(
    private inputAppMetadataDBFacade: InputAppMetadataDBFacade,
    private destroyRef: DestroyRef,
    private customLoggerService: CustomLoggerService,
  ) {}

  /**
   * Establishes a WebSocket connection to the specified URL with given parameters.
   * Handles connection status updates and automatic reconnection on failure.
   * @param url - The base WebSocket URL to connect to
   * @param params - Query parameters to append to the URL
   */
  connect(url: string, params: Record<string, string>, subjectKey: string, reason: TWebSocketConnectReason): void {
    this.#connectionInfo = [url, params, subjectKey];

    const queryParams = new URLSearchParams(params).toString();
    const fullUrl = `${url}?${queryParams}`;
    console.log('[TV DASHBOARD WEBSOCKET SERVICE]', 'Connecting to:', fullUrl);

    if (!this.socket$ || this.socket$.closed) {
      const config: WebSocketSubjectConfig<
        | TTvDashboardWebsocketRecieveMessage
        | TTvDashboardWebsocketComponentSubscribeMessage['message']
        | THearBeatMessage
      > = {
        url: fullUrl,
        openObserver: {
          next: () => {
            this.customLoggerService.log(
              '[TV_DASHBOARD_DEBUG_WEBSOCKET_CONNECTED]',
              `Connected to Web-Socket, reason: ${reason}`,
            );
            this.connectionStatus.next('CONNECTED');
          },
        },
        closeObserver: {
          next: () => {
            this.customLoggerService.log('[TV_DASHBOARD_DEBUG_WEBSOCKET_DISCONNECTED]', 'Disconnected from Web-Socket');
            this.connectionStatus.next('DISCONNECTED');
          },
        },
      };

      this.socket$ = new WebSocketSubject<
        | TTvDashboardWebsocketRecieveMessage
        | TTvDashboardWebsocketComponentSubscribeMessage['message']
        | THearBeatMessage
      >(config);

      this.socket$.subscribe({
        next: (data) => {
          if (data && 'message' in data && 'series' in data.message) {
            const series = data.message['series'];

            if (
              Array.isArray(series) &&
              series.length > 0 &&
              series[0].attribute === 'achievement_vs_target_with_color'
            ) {
              this.customLoggerService.log(
                '[TV_DASHBOARD_DEBUG_ACHIEVEMENT_VS_TARGET_WITH_COLOR]',
                JSON.stringify(data),
              );
            }
          }

          if (data && 'message' in data) {
            const message = data.message;
            if ('data_type' in message && message['data_type'] === 'table') {
              this.customLoggerService.log('[TV_DASHBOARD_DEBUG_PRODUCTION_TABLE_DATA]', JSON.stringify(data));
            }
          }

          const msgData = data as TTvDashboardWebsocketRecieveMessage;

          const channelId = msgData.channel_id;
          if (channelId && this.#watchedChannels.has(channelId)) {
            this.#watchedChannels.set(channelId, Date.now());
          }

          return this.messagesSubject$.next(msgData);
        },
        error: (error) => {
          this.customLoggerService.log('[TV_DASHBOARD_DEBUG_WEBSOCKET_ERROR]', JSON.stringify(error));
          console.error('[TV DASHBOARD WEBSOCKET SERVICE]', 'Error:', error);
        },
      });

      if (!this.#heartBeatTimer$) this.#startHeartbeat();
      if (!this.#silenceCheckTimer$) this.#startSilenceCheck();
    }
  }

  /**
   * Checks if the WebSocket connection is currently active.
   * @returns True if the socket is connected, false otherwise
   */
  isConnected(): boolean {
    return this.socket$ !== null && !this.socket$.closed;
  }

  /**
   * Sends a message through the WebSocket connection.
   * @param message - The message to send
   * @throws Console error if socket is not connected
   */
  sendMessage(message: TTvDashboardWebsocketComponentSubscribeMessage): void {
    if (this.socket$ && !this.socket$.closed) {
      this.customLoggerService.log('[TV_DASHBOARD_DEBUG_SUBSCRIBE_SENT]', JSON.stringify(message));
      this.socket$.next(message.message);
    } else {
      this.customLoggerService.log('[TV_DASHBOARD_DEBUG_SUBSCRIBE_NOT_SENT]', JSON.stringify(message));
      console.error('[TV DASHBOARD WEBSOCKET SERVICE]', 'Cannot send message: socket is not connected');
    }
  }

  /**
   * Returns an Observable that emits incoming WebSocket messages.
   * @returns Observable of TV Dashboard WebSocket messages
   */
  listen(): Observable<TTvDashboardWebsocketRecieveMessage> {
    return this.messages$;
  }

  /**
   * Registers a channel to be watched by the silence watchdog.
   * Call this every time a widget subscribes (including after reconnect).
   */
  registerChannel(channelId: string): void {
    this.#watchedChannels.set(channelId, Date.now());
  }

  /**
   * Closes the WebSocket connection and cleans up resources.
   * Resets the silence watchdog map so stale channel entries don't
   * trigger false reconnects after the next connect().
   */
  disconnect(): void {
    this.#watchedChannels.clear();
    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = null;
    }
  }

  /**
   * Disconnects the current WebSocket connection and establishes a new one.
   * @param url - The base WebSocket URL to connect to
   * @param params - Query parameters to append to the URL
   */
  #reconnect(
    url: string,
    params: Record<string, string>,
    subjectKey: string,
    reason: Exclude<TWebSocketConnectReason, 'initial'>,
  ): void {
    this.disconnect();
    this.connect(url, params, subjectKey, reason);
  }

  #createHeartbeatMessage(subjectKey: string): THeartBeatSendMessage {
    return {
      access_type: 'heart_beat',
      channel_id: 'heart_beat',
      subject_key: subjectKey,
      topic: 'heart_beat',
    };
  }

  #startSilenceCheck(): void {
    this.#silenceCheckTimer$ = timer(SILENCE_CHECK_INTERVAL_MS, SILENCE_CHECK_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.#watchedChannels.size === 0) return;

        const now = Date.now();
        const silentChannels = [...this.#watchedChannels.entries()].filter(
          ([, lastDataAt]) => now - lastDataAt > WIDGET_SILENCE_MS,
        );

        if (silentChannels.length === 0) return;

        const logMessage = {
          silentChannels: silentChannels.map(([id, lastDataAt]) => ({ id, silentMs: now - lastDataAt })),
          totalWatched: this.#watchedChannels.size,
        };

        this.customLoggerService.log('[TV_DASHBOARD_DEBUG_STALE_WIDGET_DATA]', JSON.stringify(logMessage));

        if (this.#connectionInfo && this.#isServerHealthy()) {
          this.customLoggerService.log('[TV_DASHBOARD_DEBUG_STALE_WIDGET_DATA_RECONNECT]', JSON.stringify(logMessage));

          this.#reconnect(...this.#connectionInfo, 'silence_watchdog');
        }
      });
  }

  #startHeartbeat(): void {
    this.#heartBeatTimer$ = timer(0, HEARTBEAT_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(
        switchMap(() => {
          const subjectKey = this.#connectionInfo && this.#connectionInfo[2];
          if (this.#isServerHealthy()) {
            if (this.socket$ && !this.socket$.closed && subjectKey)
              this.socket$.next(this.#createHeartbeatMessage(subjectKey));
          } else this.disconnect();

          return this.messages$.pipe(
            filter(isHeartbeatAck),
            take(1),
            timeout(HEARTBEAT_TIMEOUT_MS),
            catchError(() => {
              if (this.#connectionInfo && this.#isServerHealthy())
                this.#reconnect(...this.#connectionInfo, 'heartbeat_timeout');
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe();
  }
}
